import cx from 'classnames';
import { capitalize, get } from 'lodash';
import qs from 'qs';
import * as React from 'react';
import Modal from 'react-modal';

// @ts-ignore
import AdminStatisticsCommunicationChart from 'admin/components/AdminStatisticsCommunicationChart';
// @ts-ignore
import { formatData } from 'admin/lib/Chart';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
// @ts-ignore
import Display from 'components/Display';
import Drift from 'components/Drift';
import Footer from 'components/Footer';
// @ts-ignore
import Head from 'components/Head';
import Image from 'components/Image';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import EventTracking from 'lib/EventTracking';
import EngagementRatings from 'organizations/constants/EngagementRatings';
import OrganizationEngagementBadge from 'organizations/components/OrganizationEngagementBadge';
import OrganizationEngagementBanner from 'organizations/components/OrganizationEngagementBanner';
import OrganizationEngagementModal from 'organizations/components/OrganizationEngagementModal';
import {
  ADMIN_RECOMMENDATIONS,
  TEACHER_RECOMMENDATIONS,
} from 'organizations/constants/OrganizationEngagementViewCopy';

import OrganizationEngagementViewAdmins from './OrganizationEngagementViewAdmins';
import OrganizationEngagementViewChartFigure from './OrganizationEngagementViewChartFigure';
import OrganizationEngagementViewContainer from './OrganizationEngagementViewContainer';
import OrganizationEngagementViewContentFooter from './OrganizationEngagementViewContentFooter';
import OrganizationEngagementViewContentHeader from './OrganizationEngagementViewContentHeader';
import { Organization, Schools, Stats } from './OrganizationEngagementViewDataContainer';
import OrganizationEngagementViewEngagement from './OrganizationEngagementViewEngagement';
import OrganizationEngagementViewEngagementBox from './OrganizationEngagementViewEngagementBox';
import OrganizationEngagementViewHeading from './OrganizationEngagementViewHeading';
import OrganizationEngagementViewScreenshotFigures from './OrganizationEngagementViewScreenshotFigures';
import OrganizationEngagementViewSection from './OrganizationEngagementViewSection';
import OrganizationEngagementViewTeachers from './OrganizationEngagementViewTeachers';

import './OrganizationEngagementView.scss';

const eventKey = 'district_engagement_rating';

const chartHeight = 320;

const utmParams = qs.stringify({
  utm_campaign: 'engagement-rating',
  utm_medium: 'site',
  utm_source: 'district-pages',
});

type Props = {
  organization: Organization;
  schools?: Schools | undefined;
  stats?: Stats | undefined;
};

type State = {
  showRatingsModal: boolean;
};

export default class OrganizationEngagementView extends React.Component<Props, State> {
  state: State = {
    showRatingsModal: false,
  };

  componentDidMount() {
    EventTracking.trackKey(`${eventKey}.view`);
  }

  handleCloseRatingsModal = () => {
    this.setState({ showRatingsModal: false });
  };

  handleShowRatingsModal = () => {
    this.setState({ showRatingsModal: true });
  };

  render() {
    const { organization, schools, stats } = this.props;
    const {
      city,
      great_schools_url: greatSchoolsUrl,
      org_name: name,
      peak_teacher_student_parent: activeUsersCount,
      perc_active: schoolsActivePercent,
      perc_emerging: schoolsEmergingPercent,
      perc_engaged: schoolsEngagedPercent,
      perc_growing: schoolsGrowingPercent,
      state,
      url,
    } = organization;
    let { max_rating: schoolRating } = organization;
    const { showRatingsModal } = this.state;

    // TODO
    if (!stats) {
      return null;
    }

    // some districts set to `no rating`
    if (schoolRating && !EngagementRatings.RATINGS.includes(schoolRating)) {
      schoolRating = undefined;
    }

    return (
      <div className="OrganizationEngagementView">
        <Head
          title={name || null}
          description="See and understand engagement across your entire district with the Remind
            engagement rating, exclusive community engagement statistics, and educator-tested
            strategies for activating engagement in your organization."
          image={schoolRating ? EngagementRatings.BADGES[schoolRating].og : null}
          twitterCard
        />
        <header className="OrganizationEngagementView-header">
          <div className="OrganizationEngagementView-header-inner">
            <Link
              href={LinkConstants.MARKETING}
              target="_blank"
              className="OrganizationEngagementView-header-brand"
              eventKey={`${eventKey}.header.brand.click`}>
              <Image
                src={require('assets/images/organizations/OrganizationEngagementView/logo.svg')}
                alt="Remind"
              />
            </Link>
            <Link
              href={LinkConstants.ENGAGEMENT_RATINGS}
              weight="semibold"
              eventKey={`${eventKey}.header.cta.click`}>
              <Display hide="480">Learn about the engagement rating</Display>
              <Display show="480">Learn about the rating</Display>
            </Link>
          </div>
        </header>
        <OrganizationEngagementViewContentHeader
          city={city}
          eventKey={eventKey}
          name={name}
          schoolRating={schoolRating}
          state={state}
          onShowRatingsModal={this.handleShowRatingsModal}
        />
        <OrganizationEngagementViewSection>
          <Container>
            <OrganizationEngagementViewEngagementBox
              eventKey={eventKey}
              name={name}
              schoolRating={schoolRating}
              schools={schools}
              schoolsActivePercent={schoolsActivePercent}
              schoolsEmergingPercent={schoolsEmergingPercent}
              schoolsEngagedPercent={schoolsEngagedPercent}
              schoolsGrowingPercent={schoolsGrowingPercent}
              utmParams={utmParams}
            />
          </Container>
          <Container size="large">
            <Row size="xxxxxlarge">
              <OrganizationEngagementViewEngagement
                activeUsersCount={activeUsersCount}
                eventKey={eventKey}
                name={name}
                schoolRating={schoolRating}
                stats={stats}
                utmParams={utmParams}
              />
            </Row>
            <OrganizationEngagementViewTeachers
              eventKey={eventKey}
              schoolRating={schoolRating}
              schoolsEngagedPercent={schoolsEngagedPercent}
              utmParams={utmParams}
              onShowRatingsModal={this.handleShowRatingsModal}
            />
          </Container>
        </OrganizationEngagementViewSection>
        <OrganizationEngagementViewAdmins
          eventKey={eventKey}
          schoolRating={schoolRating}
          utmParams={utmParams}
          onShowRatingsModal={this.handleShowRatingsModal}
        />
        <OrganizationEngagementViewContentFooter
          city={city}
          eventKey={eventKey}
          greatSchoolsUrl={greatSchoolsUrl}
          name={name}
          state={state}
          url={url}
          utmParams={utmParams}
        />
        <Footer
          disclaimer={`This ${schoolRating ? 'rating' : 'page'
            } is not an endorsement of Remind by ${name}.`}
        />
        <Modal isOpen={showRatingsModal} className="OrganizationEngagementView-ratingsModal">
          <OrganizationEngagementModal
            eventKey={eventKey}
            organizationType="district"
            rating={schoolRating}
            utmParams={utmParams}
            onRequestHide={this.handleCloseRatingsModal}
          />
        </Modal>
        <Drift />
      </div>
    );
  }
}
